import styled from '@emotion/styled'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Breakpoint from '../../Breakpoint'
import Loader from '../../components/global/Loader'
import SearchInput from '../../components/global/SearchInput'
import Title from '../../components/global/Title'
import UserContext from '../../context/UserContext'
import { apiClient } from '../../services/apiClient'

import { TrainingSessionGroup } from '../../types/training-session-group.type'
import CoachTrainingSessionGroupFormModal from './CoachTrainingSessionGroupFormModal'

const StyledButton = styled(Button)`
    font-size: 1.6rem;
    margin-right: 8px;
    margin-bottom: 8px;

    &:active {
        transform: scale(0.95);
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-right: 0;
        width: 100%;
    }
`

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    width: 100%;

    @media (max-width: 750px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`

const FilterContainer = styled.div`
    display: flex;
    gap: 32px;
    margin-bottom: 28px;
    flex-wrap: wrap;
    align-items: flex-end;
    width: 100%;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        gap: 16px;
    }
`

const CoachTrainingSessionGroups: React.FC = () => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const user = useContext(UserContext)
    const [searchTerm, setSearchTerm] = useState('')

    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingGroup, setEditingGroup] = useState<TrainingSessionGroup | null>(null)

    const { data: ownedGroups = [], isLoading: isLoadingOwned } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups', 'owned'],
        queryFn: async () => {
            const { data } = await apiClient.get('/training-session-groups?type=owned')
            return data
        },
        enabled: !!user?.coachData?.id,
    })

    const { data: sharedGroups = [], isLoading: isLoadingShared } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups', 'shared'],
        queryFn: async () => {
            const { data } = await apiClient.get('/training-session-groups?type=shared')
            return data
        },
        enabled: !!user?.coachData?.id,
    })

    const groups = useMemo(() => {
        const allGroups = [...ownedGroups, ...sharedGroups]
        const uniqueGroups = allGroups.filter(
            (group, index, self) => index === self.findIndex((g) => g.id === group.id),
        )
        return uniqueGroups
    }, [ownedGroups, sharedGroups])

    const isLoading = isLoadingOwned || isLoadingShared

    const filteredGroups = groups
        .filter((group) => group.title !== 'Aucun groupe')
        .filter((group) => {
            const matchesSearch =
                group.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (group.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
            return matchesSearch
        })

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            await apiClient.delete(`/training-session-groups/${id}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['trainingSessionGroups'] })
            toast.success(t('coach.trainingSessionGroups.deleteSuccess'))
        },
        onError: () => {
            toast.error(t('coach.trainingSessionGroups.deleteError'))
        },
    })

    const handleEdit = (group: TrainingSessionGroup) => {
        setEditingGroup(group)
        setIsFormModalOpen(true)
    }

    const handleCreate = () => {
        setEditingGroup(null)
        setIsFormModalOpen(true)
    }

    const handleCloseModal = useCallback(() => {
        setIsFormModalOpen(false)
        setEditingGroup(null)
    }, [])

    if (isLoading) {
        return <Loader />
    }

    return (
        <div className="container-fluid py-4">
            <TitleWrapper>
                <Title>{t('coach.trainingSessionGroups.title')}</Title>

                <StyledButton variant="primary" onClick={handleCreate}>
                    {t('common.create')}
                </StyledButton>
            </TitleWrapper>

            <SearchInput
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Row className="g-4">
                {filteredGroups.map((group) => (
                    <Col key={group.id} xs={12} md={6} lg={4}>
                        <Card className="h-100">
                            <Card.Body>
                                <Card.Title style={{ fontSize: '1.6rem', marginBottom: '10px', fontWeight: '600' }}>
                                    {group.title}
                                </Card.Title>

                                {group.description && <Card.Text className="mb-2">{group.description}</Card.Text>}

                                <div
                                    className="mb-3"
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}
                                >
                                    <i className="fas fa-users" style={{ width: '16px' }}></i>
                                    <span>
                                        {group.members?.length ?? 0} {t('coach.trainingSessionGroups.members')}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                    <StyledButton variant="outline-primary" size="sm" onClick={() => handleEdit(group)}>
                                        {t('common.edit')}
                                    </StyledButton>
                                    <StyledButton
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => {
                                            if (window.confirm(t('common.confirmDelete'))) {
                                                deleteMutation.mutate(group.id)
                                            }
                                        }}
                                        disabled={deleteMutation.isPending}
                                    >
                                        {deleteMutation.isPending ? (
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            t('common.delete')
                                        )}
                                    </StyledButton>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <CoachTrainingSessionGroupFormModal show={isFormModalOpen} onHide={handleCloseModal} group={editingGroup} />
        </div>
    )
}

export default CoachTrainingSessionGroups
